import axios from 'axios';
import React, { memo, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getText } from '@storebrand-digital/fms-translations';
import { Button, Chip } from '@storebrand-digital/pretzel';
import { useQueryClient } from '@tanstack/react-query';
import { format, subYears } from 'date-fns';
import { getDatadogLogger } from '@storebrand-digital/common-datadog';

import Filter from '../Filter';
import Document from '../Document';
import { DocumentProvider } from '../Document/context';
import MyOtherArchives from './MyOtherArchives';
import { selectFilters } from '../Filter/selectors';
import useDerivedSearchParams from '../Filter/hooks/useDerivedSearchParams';
import useUrlFilterSync from '../Filter/hooks/useUrlFilterSync';
import useCategoryMapping from '../Filter/hooks/useCategoryMapping';
import styles from './Dashboard.module.css';
import { filterActions } from '../Filter/reducer';
import { LayoutContainer } from '../../app/LayoutContainer';
import InsuranceArchive from './InsuranceArchive';
import { fetchDocumentsByFilters } from '../Document/hooks/queries';
import { useAppSelector } from '../../app/store';
import { SegmentChipProps } from './types';
import { archiveSegments, Segment, SegmentId } from '../../utils/segments';
import useBankCustomer from '../../app/hooks/useBankCustomer';
import { useKron } from '../../app/hooks/useKron';
import { isKronWebView } from '@storebrand-digital/kron-web-view-helpers';
import { isKronWebApp } from '../../utils';

const usePrefetchBankSegment = (
  isBankCustomer: boolean | undefined,
  selectedSegmentId: string
) => {
  const queryClient = useQueryClient();

  function trackSegmentUrlOpen(selectedSegmentId: string) {
    window.dataLayer?.push({
      event: 'universal-event',
      eventModel: {
        event_category: 'Segment url open',
        event_action: `segment-${selectedSegmentId}`,
        event_label: 'Segment paged opened for customer',
      },
    });
  }

  useEffect(() => {
    trackSegmentUrlOpen(selectedSegmentId);
    if (isBankCustomer && selectedSegmentId !== Segment.BANK) {
      const prefetchFilter = {
        page: 1,
        size: 15,
        segment: Segment.BANK,
        createdFrom: format(subYears(new Date(), 1), 'yyyy-MM-dd'),
        createdTo: format(new Date(), 'yyyy-MM-dd'),
        categories: [],
        contractNumbers: [],
        products: [],
      };
      // Use the same key structure as in useDocumentsWithPagination
      const queryKey = [
        'documents',
        Segment.BANK,
        prefetchFilter.page,
        prefetchFilter.size,
        prefetchFilter.createdFrom,
        prefetchFilter.createdTo,
        null, // contractNumbers
        null, // products
        null, // categories
        undefined, // sortBy
        undefined, // sortDirection
      ];
      const data = queryClient.getQueryData(queryKey);
      const isFetching = queryClient.isFetching({ queryKey, exact: true });

      if (data || isFetching) {
        return;
      }

      queryClient.prefetchQuery({
        queryKey,
        queryFn: () => fetchDocumentsByFilters(prefetchFilter, Segment.BANK),
      });
    }
  }, [queryClient, isBankCustomer, selectedSegmentId]);
};

const useNotificationReadStatus = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    axios
      .patch('/customer/notifications/read?eventType=document-archived')
      .catch((e) => {
        getDatadogLogger().error(
          'Failed to mark notifications as read',
          undefined,
          e
        );
      });
  }, [dispatch]);
};

const getSegmentTrackId = (segmentId: SegmentId) => {
  switch (segmentId) {
    case Segment.MAIN:
      return 'button-segment_main|Hovedarkiv';
    case Segment.BANK:
      return 'button-segment_bank|Bank';
    case Segment.INSURANCE:
      return 'button-segment_faktura|Faktura';
    case Segment.OTHER:
      return 'button-segment_other|Andre';
    default:
      return `button-segment_${segmentId}`;
  }
};

const SegmentChip = memo(
  ({ segment, isSelected, onSelect }: SegmentChipProps) => (
    <Chip
      key={segment.id}
      id={`chips-${segment.id}`}
      name={`${segment.id}-archive`}
      label={getText(segment.label)}
      value={segment.id}
      iconLeft={segment.icon}
      variant="input"
      checked={isSelected}
      onValueChange={() => onSelect(segment.id)}
      labelProps={{ 'data-trackid': getSegmentTrackId(segment.id) }}
    />
  )
);

function Dashboard() {
  const filters = useAppSelector(selectFilters);
  const { derivedSearchParams, origin } = useDerivedSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedSegmentId = filters.segment;
  const hasContractNumbers = !!filters.contractNumbers?.length;
  const { data: isBankCustomer } = useBankCustomer();
  const { isKron } = useKron();

  useNotificationReadStatus();
  useCategoryMapping();
  useUrlFilterSync();
  usePrefetchBankSegment(isBankCustomer, selectedSegmentId);

  // Update URL when derivedSearchParams changes
  useEffect(() => {
    navigate(`?${derivedSearchParams}`, { replace: true });
  }, [navigate, derivedSearchParams]);

  const handleSegmentChange = useCallback(
    (segmentId: SegmentId) => {
      dispatch(filterActions.changeSegment(segmentId));
    },
    [dispatch]
  );

  const documentView = () => (
    <>
      {!isKronWebView && selectedSegmentId == Segment.BANK && <Filter.Root />}
      <Document.Alert />
      <Document.Table />
      <Document.Pagination />
    </>
  );

  const renderContent = () => {
    switch (selectedSegmentId) {
      case Segment.MAIN:
      case Segment.BANK:
        return (
          <DocumentProvider selectedSegmentId={selectedSegmentId}>
            {documentView()}
          </DocumentProvider>
        );
      case Segment.INSURANCE:
        return <InsuranceArchive />;
      case Segment.OTHER:
        return <MyOtherArchives />;
      default:
        return (
          <DocumentProvider selectedSegmentId={Segment.MAIN}>
            {documentView()}
          </DocumentProvider>
        );
    }
  };

  return (
    <LayoutContainer>
      <div className={styles.dashboardBase}>
        <div className={styles.sectionTitle}>
          {!isKronWebApp && (origin || hasContractNumbers) && (
            <Button
              className={styles.backButton}
              variant="subtle"
              iconLeft="arrow-left"
              onClick={() => navigate(-1)}
              size="medium"
            >
              {getText('goBackLink.label')}
            </Button>
          )}
          <h1>
            {origin
              ? hasContractNumbers
                ? getText('breadcrumb.related.title')
                : getText(`breadcrumb.${origin}.title`)
              : getText(isKron ? 'header.kron.title' : 'header.title')}
          </h1>
        </div>
        {!isKronWebView && !(origin || hasContractNumbers) && (
          <div className={styles.segmentedChipsBase}>
            {archiveSegments
              .filter((segment) => segment.showInKron || !isKron)
              .map((segment) => (
                <SegmentChip
                  key={segment.id}
                  segment={segment}
                  isSelected={selectedSegmentId === segment.id}
                  onSelect={handleSegmentChange}
                />
              ))}
          </div>
        )}
        <div className={styles.dashboardContent}>{renderContent()}</div>
      </div>
    </LayoutContainer>
  );
}

export default Dashboard;
